import React from "react";
import type { PainelBordeiroSemana } from "../../../services/painelBordeiroService";
import { formatDiaMes } from "../utils";

type Props = {
  dados: PainelBordeiroSemana;
  onSelecionarDia: (data: string) => void;
};

const VisaoSemanal: React.FC<Props> = ({ dados, onSelecionarDia }) => {
  if (dados.bordas.length === 0) {
    return (
      <p className="text-sm text-neutral-600">
        Não há vendas de bordas registradas na semana anterior.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-neutral-500">
        Cada célula mostra a sugestão ({dados.percentual}% do mesmo dia da
        semana anterior). Quando a produção do dia já foi confirmada, o número
        confirmado aparece em destaque e a sugestão fica abaixo. Clique no dia
        para abrir a visão diária.
      </p>
      <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-neutral-200 text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="sticky left-0 bg-neutral-50 px-4 py-3 text-left font-semibold text-neutral-700">
                Borda
              </th>
              {dados.dias.map((d) => (
                <th
                  key={d.data}
                  className={`px-3 py-2 text-center font-semibold ${
                    d.data === dados.hoje
                      ? "bg-primary/10 text-primary"
                      : "text-neutral-700"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelecionarDia(d.data)}
                    className="flex w-full flex-col items-center hover:underline"
                  >
                    <span>{d.diaSemana}</span>
                    <span className="text-xs font-normal text-neutral-500">
                      {formatDiaMes(d.data)}
                    </span>
                    {d.producaoStatus && (
                      <span
                        className={`mt-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          d.producaoStatus === "finalizada"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {d.producaoStatus === "finalizada"
                          ? "Finalizada"
                          : "Em produção"}
                      </span>
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {dados.bordas.map((b) => (
              <tr key={b.bordaChave} className="hover:bg-neutral-50/60">
                <td className="sticky left-0 bg-white px-4 py-2.5 font-medium text-neutral-900">
                  {b.bordaNome}
                </td>
                {dados.dias.map((d) => {
                  const c = b.dias[d.data];
                  return (
                    <td
                      key={d.data}
                      className={`px-3 py-2.5 text-center tabular-nums ${
                        d.data === dados.hoje ? "bg-primary/5" : ""
                      }`}
                    >
                      {c?.planejada != null ? (
                        <>
                          <span className="font-bold text-neutral-900">
                            {c.planejada}
                          </span>
                          <span className="block text-[11px] text-neutral-400">
                            sug. {c.sugerida}
                          </span>
                        </>
                      ) : (
                        <span className="text-neutral-800">
                          {c?.sugerida ?? 0}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-neutral-50 font-semibold">
            <tr>
              <td className="sticky left-0 bg-neutral-50 px-4 py-3 text-neutral-800">
                Total
              </td>
              {dados.dias.map((d) => (
                <td key={d.data} className="px-3 py-3 text-center tabular-nums">
                  {d.totalPlanejada ?? d.totalSugerida}
                  <span className="block text-[11px] font-normal text-neutral-400">
                    vendido {d.totalBase}
                  </span>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default VisaoSemanal;
